/**
 * Native Capacitor bootstrap.
 *
 * Initialises native plugins (status bar, splash screen, back button,
 * push notifications) when the app is running inside the Capacitor shell.
 * On the web (regular browser) this file is a no-op.
 *
 * Call `initNative()` once at app boot.
 */
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { PushNotifications } from "@capacitor/push-notifications";
import API from "@/lib/api";

export const isNative = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

async function configureStatusBar() {
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    if (nativePlatform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#050A14" });
    }
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch (e) {
    console.warn("StatusBar config failed", e);
  }
}

async function hideSplash() {
  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch (e) { /* ignore */ }
}

/**
 * Wire Android hardware back button to history.back() if there's history,
 * or exit the app at root.
 */
function wireBackButton() {
  CapApp.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      CapApp.exitApp();
    }
  });
}

/**
 * Request push permissions, register the device with APNS/FCM, and
 * forward the resulting token to the backend so it can be used for
 * native push delivery.
 */
async function registerForPush() {
  try {
    const perm = await PushNotifications.checkPermissions();
    let granted = perm.receive === "granted";
    if (!granted) {
      const req = await PushNotifications.requestPermissions();
      granted = req.receive === "granted";
    }
    if (!granted) return;

    await PushNotifications.register();

    PushNotifications.addListener("registration", async ({ value: token }) => {
      try {
        await API.post("/push/native-token", {
          token,
          platform: nativePlatform(), // 'ios' | 'android'
        });
        console.log("native push token registered");
      } catch (err) {
        console.warn("backend native-token register failed", err);
      }
    });

    PushNotifications.addListener("registrationError", err => {
      console.warn("push registrationError", err);
    });

    // When user taps a notification while app is in background
    PushNotifications.addListener("pushNotificationActionPerformed", evt => {
      const url = evt?.notification?.data?.url;
      if (url) window.location.assign(url);
    });
  } catch (e) {
    console.warn("registerForPush failed", e);
  }
}

let initialized = false;
export async function initNative() {
  if (initialized || !isNative()) return;
  initialized = true;

  document.documentElement.classList.add(`native-${nativePlatform()}`);

  await configureStatusBar();
  wireBackButton();

  // Hide splash after first paint
  setTimeout(hideSplash, 600);

  // Defer push registration until after auth so we can attach the token
  // to a logged-in user; consumers can call `registerForPush` manually
  // from the auth flow.
}

export { registerForPush };
